import { Button, Dialog } from '@/components/UI'
import { validateApplicationFormFieldValue } from '@/schemas/forms/applicationFormField'
import type { ApplicationFormData } from '@/schemas/v2'
import { useMemo, useState } from 'react'
import type { FormEvent } from 'react'
import { ApplicationFormField } from './ApplicationFormField'

type Props = {
  opened: boolean
  applicationForm: ApplicationFormData
  onSubmit: (formData: Record<string, string | string[]>) => void
  onClose: () => void
  /** サーバーサイドバリデーションエラー（422 レスポンスの errors フィールド）。キー: fieldKey、値: エラーメッセージ */
  serverErrors?: Record<string, string>
}

export function DialogV2ApplicationForm({ opened, applicationForm, onSubmit, onClose, serverErrors }: Props) {
  const [values, setValues] = useState<Record<string, string | string[]>>(() => {
    const initial: Record<string, string | string[]> = {}
    for (const field of applicationForm.fields) {
      initial[field.fieldKey] = applicationForm.prefill?.[field.fieldKey] ?? (field.inputType === 'checkbox' ? [] : '')
    }
    return initial
  })

  // 一度でも blur されたフィールドキーを記録（blur 前はエラーを表示しない）
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set())

  const handleChange = (fieldKey: string, value: string | string[]) => {
    setValues((prev) => ({ ...prev, [fieldKey]: value }))
  }

  const handleBlur = (fieldKey: string) => {
    setTouchedFields((prev) => new Set([...prev, fieldKey]))
  }

  // 全フィールドのクライアントサイドエラーマップ（touched 有無に関わらず計算）
  const allClientErrors = useMemo(() => {
    const errors: Record<string, string> = {}
    for (const field of applicationForm.fields) {
      const error = validateApplicationFormFieldValue(field, values[field.fieldKey] ?? '')
      if (error !== null) {
        errors[field.fieldKey] = error
      }
    }
    return errors
  }, [applicationForm.fields, values])

  // touched 済みフィールドのみエラーを表示
  const visibleClientErrors = useMemo(() => {
    const errors: Record<string, string> = {}
    for (const fieldKey of touchedFields) {
      if (allClientErrors[fieldKey]) {
        errors[fieldKey] = allClientErrors[fieldKey]
      }
    }
    return errors
  }, [allClientErrors, touchedFields])

  const isValid = Object.keys(allClientErrors).length === 0

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    // 全フィールドを touched にしてエラーを一覧表示させる
    setTouchedFields(new Set(applicationForm.fields.map((f) => f.fieldKey)))
    if (!isValid) return
    onSubmit(values)
  }

  // ダイアログを閉じる際に touchedFields をリセットする。
  // このコンポーネントはアンマウントされないため、リセットしないと
  // 再オープン時に即クライアントエラーが表示される。
  const handleClose = () => {
    setTouchedFields(new Set())
    onClose()
  }

  return (
    <Dialog
      selectorState={opened}
      closeFunc={handleClose}
      title="お客様情報の入力"
      showCloseButton={false}
      closeOnClickOutside={false}
    >
      {/* noValidate でブラウザネイティブバリデーション吹き出しを無効化 */}
      <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
        {applicationForm.fields
          .slice()
          .sort((a, b) => a.sortOrder - b.sortOrder)
          .map((field) => (
            <ApplicationFormField
              key={field.id}
              field={field}
              value={values[field.fieldKey] ?? (field.inputType === 'checkbox' ? [] : '')}
              onChange={handleChange}
              onBlur={handleBlur}
              clientError={visibleClientErrors[field.fieldKey]}
              serverError={serverErrors?.[field.fieldKey]}
            />
          ))}
        <div className="mt-2 flex flex-col gap-2">
          <Button label="確認する" variant="primary" size="md" fullWidth type="submit" disabled={!isValid} />
          <button
            type="button"
            onClick={handleClose}
            className="flex h-[48px] w-full items-center justify-center rounded-[8px] border border-gray-300 text-[15px] font-bold text-gray-600"
          >
            キャンセル
          </button>
        </div>
      </form>
    </Dialog>
  )
}
